import { ExclamationCircleOutlined } from "@ant-design/icons";
import { SalePlan, SalePlanOption } from "@api/saleApi";
import { ContractMode } from "@api/utils/normalizeContract";
import PopupBackground from "@component/PopupBackground";
import { StaffPermission, usePermission } from "@hooks/usePermission";
import { fetchCreateChangePrice, fetchUpdateChangePrice, SaleState } from "@redux/saleSlice";
import { debounceAsync } from "@utils/common";
import MathRound from "@utils/MathRound";
import mathUtils from "@utils/mathUtils";
import {
  Button,
  Typography,
  DatePicker,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Modal,
  message,
  Col,
} from "antd";
import locale from "antd/es/date-picker/locale/zh_TW";
import { RangePickerProps } from "antd/lib/date-picker";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import fontStyle from "src/styles/fontStyle";
import styled from "styled-components";
import priceUtils, { AllocateDataMap, AllocatePriceModal, getExpGrossMarginPayload } from "./utils/utils";

const Wrapper = styled.div`
  position: relative;
  min-width: 800px;
  max-width: 80vw;
  padding: 20px;
  background: ${({ theme }) => theme.colorNeutral100};
  max-height: 90vh;
  overflow: auto;
`;
const CustomInputNumber = styled(InputNumber)``;
const CustomRow = styled(Row)`
  margin-bottom: 12px;
`;
const CustomRadio = styled(Radio)<{ marginBottom?: string }>`
  display: block;
  margin-bottom: ${({ marginBottom }) => marginBottom};
`;

const CustomSelect = styled(Select)`
  width: 100%;
`;

const CustomInput = styled(Input)<{ width?: string; gap?: boolean; error?: boolean }>`
  width: ${({ width }) => width || "52px"};
  margin: ${({ gap }) => gap && "0px 10px"};
  border: ${({ error }) => error && "2px solid #ec6922"};
`;

const Text = styled.span`
  margin-right: 30px;
  min-width: 100px;
  padding: 8px 0;
  ${fontStyle("14px", "16px")};
`;

const TableWrapper = styled.div`
  margin-bottom: 12px;
`;

const Error = styled.span`
  color: #ec6922;
  ${fontStyle("14px", "16px")};
`;

const QtyText = styled.span`
  font-weight: bold;
`;

interface Option {
  id: number;
  name: string;
  vpc: {
    id: number;
    price: number;
    commissionRate: number;
    productName: string;
    contractInfo: {
      id: number;
      mode: ContractMode;
      modeName: string;
    };
  };
  groupQty: number;
  unitPrice: number;
  unitCost: number;
  canBuyCount: number;
}

enum RadioType {
  DEFAULT = 1,
  TIMING = 2,
}

type Props = {
  isLast: boolean;
  close: () => void;
  onNext: () => void;
  salePlanListResult: SalePlan[];
  getSalePlanList: () => void;
};

const specialPriceModal = AllocatePriceModal();
const vipPriceModal = AllocatePriceModal();

export default function ChangePriceModal(props: Props) {
  const { isLast, close, onNext, salePlanListResult, getSalePlanList } = props;
  const dispatch = useDispatch();
  const { needReload, changePriceError, currentPlanChangePrice, currentSalePlan } = useSelector(SaleState);
  const modifyCommissionPermission = usePermission(StaffPermission.CAN_COMMISSION_RATE_DISCOUNT);
  const [specialPriceMap, setSpecialPriceMap] = useState<AllocateDataMap>(new Map()); // 保存計算後的優惠價map
  const [vipPriceMap, setVipPriceMap] = useState<AllocateDataMap>(new Map()); // 保存計算後的黑卡價map

  const [specialPrice, setSpecialPrice] = useState<number>();
  const [rateMap, setRateMap] = useState<Map<number, number>>(new Map());
  const [salePlanMap, setSalePlanMap] = useState<Map<number, SalePlan>>(new Map());
  const [salePlan, setSalePlan] = useState<SalePlan>(); // 選擇的方案
  const [optionDiscountPeriod, setOptionDiscountPeriod] = useState<moment.Moment[]>([]);
  const [vipPrice, setVipPrice] = useState<number>();
  const [VIPGrossMargin, setVIPGrossMargin] = useState<number>();
  const [radio, setRadio] = useState<RadioType>(RadioType.DEFAULT);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [updateOptions, setUpdateOptions] = useState<SalePlanOption[]>([]);
  const columns = [
    {
      key: "name",
      dataIndex: "name",
      title: "名稱",
      render: (name: string, others: Option) => (
        <div>
          <span>{`${name}`}</span>
          <QtyText>{` x ${others.groupQty}`}</QtyText>
        </div>
      ),
    },
    {
      key: "unitPrice",
      dataIndex: "unitPrice",
      title: "售價",
      render: (unitPrice: number, others: Option) => unitPrice * others.groupQty,
    },
    {
      key: "specialPrice",
      dataIndex: "specialPrice",
      width: 100,
      title: "優惠價",
      render: (_: any, others: Option) => specialPriceMap.get(others.id)?.discountPrice ?? "-",
    },
    {
      key: "vipPrice",
      dataIndex: "vipPrice",
      width: 100,
      title: "黑卡價",
      render: (_: any, others: Option) => vipPriceMap.get(others.id)?.discountPrice ?? "-",
    },
    {
      key: "unitCost",
      dataIndex: "unitCost",
      title: "成本",
      render: (unitCost: number, others: Option) => {
        const { mode } = others.vpc.contractInfo;
        return mode === ContractMode.Vendor || mode === ContractMode.Warehouse ? (
          <div>N/A</div>
        ) : (
          <div>{mathUtils.floor(unitCost * others.groupQty, 2)}</div>
        );
      },
    },
    {
      key: "commissionRate",
      dataIndex: "commissionRate",
      title: "抽成",
      width: 160,
      render: (_: any, others: Option) => {
        const { mode } = others.vpc.contractInfo;
        return mode === ContractMode.Vendor || mode === ContractMode.Warehouse ? (
          <Row align="middle">
            <div>{`${others.vpc.commissionRate}% -  `}</div>
            <CustomInput
              error={rateMap.get(others.id) ? others.vpc.commissionRate < rateMap.get(others.id)! : false}
              disabled={!modifyCommissionPermission}
              type="number"
              min={0}
              value={rateMap.get(others.id)}
              onChange={(e) => setRateMap(new Map(rateMap.set(others.id, parseFloat(e.target.value))))}
              style={{ marginLeft: "10px" }}
            />
            <div>%</div>
          </Row>
        ) : (
          <div>N/A</div>
        );
      },
    },
  ];
  const disabledDate: RangePickerProps["disabledDate"] = (current: moment.Moment) => {
    return current && current < moment().startOf("day");
  };

  const handleOnSpecialPriceChange = (value: any) => {
    setSpecialPrice(value);

    if (typeof value === "number" && salePlan) {
      const result = specialPriceModal.allocatePriceByOriginalRatio({ price: salePlan.price, discountPrice: value });
      setSpecialPriceMap(result);
      setVipPrice(undefined);
      return;
    }
    if (value === null) {
      setVipPrice(undefined);
      setSpecialPriceMap(new Map());
      setVipPriceMap(new Map());
    }
  };

  const handleOnVIPPriceChange = (value: any) => {
    setVipPrice(value);
    if (typeof value === "number" && salePlan && specialPrice) {
      const result = vipPriceModal.allocatePriceByOriginalRatio({
        price: specialPrice,
        discountPrice: value,
      });
      setVipPriceMap(result);
      return;
    }

    if (value === null) {
      setVipPriceMap(new Map());
    }
  };
  const onCancel = () => {
    close();
    setErrorMessage("");
  };

  const onSubmitCheck = () => {
    // 黑卡試算毛利率<最低限額時，跳警告
    if (VIPGrossMargin && VIPGrossMargin < 0) {
      Modal.confirm({
        title: <Typography.Text type="danger">警告：</Typography.Text>,
        icon: <ExclamationCircleOutlined />,
        content: (
          <Space direction="vertical">
            <Typography.Text type="danger">您目前設定的黑卡價將導致負毛利率（虧損）。</Typography.Text>
            <Typography.Text type="danger">請確認是否繼續操作。</Typography.Text>
          </Space>
        ),
        onOk: () => onSubmit(),
        okText: "確認",
        cancelText: "取消",
      });
      return;
    }
    onSubmit();
  };

  const onSubmit = () => {
    if (!salePlan) return;

    setErrorMessage("");
    const validateData = {
      price: salePlan.price,
      specialPrice,
      periodType: radio,
      startAt: optionDiscountPeriod[0]?.format("YYYY-MM-DD HH:mm:ss"),
      endAt: optionDiscountPeriod[1]?.format("YYYY-MM-DD HH:mm:ss"),
      vipPrice,
    };

    const { status, message: errMsg } = priceUtils.changePriceValidator.planField(validateData);
    if (!status) {
      setErrorMessage(errMsg);
      return;
    }

    if (currentPlanChangePrice && updateOptions.length === 0) {
      const options = currentPlanChangePrice.optionsInfo.map((item) => ({
        optionId: item.optionId,
        specialPrice: specialPriceMap.get(item.optionId)!.discountPrice,
        vipPrice: vipPriceMap.get(item.optionId)!.discountPrice, // 雖然有傳值，但後端不會記錄
        commissionRateDiscount: MathRound(rateMap.get(item.optionId) || 0, 1),
        optionsDiscountsId: item.optionsDiscountsId,
        isGift: priceUtils.changePriceValidator.isGift(item),
      }));
      const { status: statusError, message: msg } = priceUtils.changePriceValidator.optionField(options);
      if (!statusError) {
        setErrorMessage(msg);
        return;
      }

      dispatch(
        fetchUpdateChangePrice({
          salesPlan: salePlan.id,
          startAt: validateData.startAt,
          endAt: validateData.endAt,
          specialPrice,
          vipPrice,
          planDiscountId: currentPlanChangePrice.planDiscountId,
          optionsDiscounts: currentPlanChangePrice.optionsInfo.map((item) => ({
            optionId: item.optionId,
            specialPrice: specialPriceMap.get(item.optionId)!.discountPrice,
            vipPrice: vipPriceMap.get(item.optionId)!.discountPrice, // 雖然有傳值，但後端不會記錄
            commissionRateDiscount: MathRound(rateMap.get(item.optionId) || 0, 1),
            optionsDiscountsId: item.optionsDiscountsId,
          })),
        }),
      );
    } else {
      const optionList = updateOptions.length > 0 ? updateOptions : salePlan.options;
      const options = optionList.map((item) => {
        return {
          optionId: item.id,
          specialPrice: specialPriceMap.get(item.id)!.discountPrice,
          vipPrice: vipPriceMap.get(item.id)!.discountPrice, // 雖然有傳值，但後端不會記錄
          commissionRateDiscount: MathRound(rateMap.get(item.id) || 0, 1),
          isGift: priceUtils.changePriceValidator.isGift(item),
        };
      });

      const { status: statusError, message: msg } = priceUtils.changePriceValidator.optionField(options);
      if (!statusError) {
        setErrorMessage(msg);
        return;
      }
      dispatch(
        fetchCreateChangePrice({
          salesPlan: salePlan!.id,
          startAt: radio === RadioType.TIMING ? optionDiscountPeriod[0].format("YYYY-MM-DD HH:mm:ss") : undefined,
          endAt: radio === RadioType.TIMING ? optionDiscountPeriod[1].format("YYYY-MM-DD HH:mm:ss") : undefined,
          specialPrice,
          vipPrice,
          commissionRateDiscount: undefined,
          optionsDiscounts: optionList.map((item) => ({
            optionId: item.id,
            specialPrice: specialPriceMap.get(item.id)!.discountPrice,
            vipPrice: vipPriceMap.get(item.id)!.discountPrice,
            commissionRateDiscount: MathRound(rateMap.get(item.id) || 0, 1),
          })),
        }),
      );
    }
  };

  const debounceExpMargin = useMemo(
    () =>
      debounceAsync(async (payload: getExpGrossMarginPayload) => {
        const expMargin = await priceUtils.getExpGrossMargin(payload);
        if (expMargin !== null) {
          setVIPGrossMargin(expMargin);
        }
      }, 500),
    [],
  );

  // 重新刷新方案列表，因為有可能新增方案
  useEffect(() => {
    getSalePlanList();
  }, [getSalePlanList]);

  // 編輯初始化
  useEffect(() => {
    if (currentPlanChangePrice) {
      if (currentPlanChangePrice.startAt) {
        setRadio(RadioType.TIMING);
        setOptionDiscountPeriod([moment(currentPlanChangePrice.startAt), moment(currentPlanChangePrice.endAt)]);
      } else {
        setRadio(RadioType.DEFAULT);
      }

      const { optionsInfo } = currentPlanChangePrice;
      setSalePlan(salePlanMap.get(currentPlanChangePrice.id)!);
      setSpecialPrice(currentPlanChangePrice.specialPrice);
      setVipPrice(currentPlanChangePrice.vipPrice);

      // 設置變價內容
      const commissionMap = new Map();
      const specialMap = new Map();
      let vipMap = new Map();
      let notAllocatedVIP = false;
      // 根據現有資料重組SpecialPriceMap & VipPriceMap:
      // 後端不會儲存option vipPrice, 雖然有接vipPriceToShow, 但實際上沒有值
      optionsInfo.forEach((item) => {
        const itemPrice = item.optionUnitPrice * item.groupQty;
        const priceRatio = currentPlanChangePrice.price > 0 ? itemPrice / currentPlanChangePrice.price : 0;
        specialMap.set(item.optionId, {
          price: itemPrice,
          discountPrice: item.specialPriceToShow,
          priceRatio,
        });

        vipMap.set(item.optionId, {
          price: itemPrice,
          discountPrice: item.vipPriceToShow, // vipPriceToShow 會是 undefined
          priceRatio,
        });

        if (!item.vipPriceToShow) notAllocatedVIP = true;

        commissionMap.set(item.optionId, item.commissionRateDiscount);
      });
      specialPriceModal.setOriginPriceMap(specialMap);
      vipPriceModal.setOriginPriceMap(vipMap);
      // 如果沒有分配黑卡價，則重新計算
      if (notAllocatedVIP) {
        vipMap = vipPriceModal.allocatePriceByOriginalRatio({
          price: currentPlanChangePrice.specialPrice,
          discountPrice: currentPlanChangePrice.vipPrice,
        });
        vipPriceModal.setOriginPriceMap(vipMap);
      }
      setRateMap(commissionMap);
      setSpecialPriceMap(specialMap);
      setVipPriceMap(vipMap);
    }
  }, [currentPlanChangePrice, salePlanMap]);

  // 新增變價初始化
  useEffect(() => {
    if (!currentPlanChangePrice) {
      const selectedSalePlan = salePlan || salePlanListResult[0];
      setSalePlan(currentSalePlan || selectedSalePlan);
      const specialMap = new Map();
      const vipMap = new Map();

      // 設置變價內容售價 price
      selectedSalePlan?.options.forEach((item) => {
        const itemPrice = item.unitPrice * item.groupQty;
        // 如果方案售價為0，則priceRatio為0
        const priceRatio = selectedSalePlan.price > 0 ? itemPrice / selectedSalePlan.price : 0;

        specialMap.set(item.id, {
          price: itemPrice,
          discountPrice: null,
          priceRatio,
        });
        vipMap.set(item.id, {
          price: itemPrice,
          discountPrice: null,
          priceRatio,
        });
      });
      specialPriceModal.setOriginPriceMap(specialMap);
      vipPriceModal.setOriginPriceMap(vipMap);
    }

    salePlanListResult.forEach((item) => {
      setSalePlanMap((prev) => new Map(prev.set(item.id, item)));
    });
  }, [currentPlanChangePrice, salePlanListResult, currentSalePlan, salePlan]);

  useEffect(() => {
    // 新建初始化
    if (salePlan) {
      salePlan.options.forEach((item) => {
        setRateMap((prev) => (prev.get(item.id) ? prev : new Map(prev.set(item.id, 0))));
      });
    }
  }, [salePlan, currentPlanChangePrice]);

  useEffect(() => {
    if (salePlan) {
      setErrorMessage("");

      if (changePriceError) {
        setErrorMessage(changePriceError);
      }
    }
  }, [salePlan, changePriceError]);

  useEffect(() => {
    if (needReload) {
      onNext();
    }
  }, [needReload, onNext]);

  useEffect(() => {
    // 多繞這一層的原因是
    // 如果新增變價過後又想增加 plan option，照上面的邏輯會進到 update，但是 update 沒有 optionsDiscountsId
    // 這樣會有問題，所以新增 plan option 就算在 create 那邊
    if (salePlan && currentPlanChangePrice) {
      const set = new Set();
      currentPlanChangePrice.optionsInfo.forEach((item) => {
        set.add(item.optionId);
      });
      salePlan.options.forEach((item) => {
        if (!set.has(item.id)) {
          setUpdateOptions((prev) => [...prev, item]);
        }
      });
    }
  }, [salePlan, currentPlanChangePrice]);

  // 帶入試算毛利率
  useEffect(() => {
    if (!vipPrice) {
      debounceExpMargin.cancel();
      setVIPGrossMargin(undefined); // 清除毛利率
      return;
    }

    if (vipPrice && salePlan) {
      const { options } = salePlan;
      const spPriceMap: Record<string, number> = {};
      specialPriceMap.forEach((item, key) => {
        const optionSku = options.find((opt) => opt.id === key)?.vpc.sku;
        if (optionSku) spPriceMap[optionSku] = item.discountPrice;
      });
      try {
        const startAt = optionDiscountPeriod[0]?.format("YYYY-MM-DD");
        debounceExpMargin({
          planId: salePlan.id,
          newPriceD: spPriceMap,
          expVipPrice: vipPrice,
          startAt,
        });
      } catch (err: any) {
        message.error(err.message);
      }
    }
  }, [debounceExpMargin, optionDiscountPeriod, salePlan, specialPriceMap, vipPrice]);

  return (
    <PopupBackground close={onCancel} fixed>
      <Wrapper>
        <CustomRow align="middle" wrap={false}>
          <Text>方案名稱</Text>
          <CustomSelect
            value={salePlan?.id}
            onChange={(id) => setSalePlan(salePlanMap.get(parseInt(id as string, 10))!)}
          >
            {salePlanListResult.map((item) => (
              <Select.Option key={item.name} value={item.id}>
                {item.name}
              </Select.Option>
            ))}
          </CustomSelect>
        </CustomRow>
        <CustomRow>
          <Text>變價內容</Text>
        </CustomRow>
        <TableWrapper>
          <Table columns={columns} dataSource={salePlan?.options} pagination={false} />
        </TableWrapper>
        <CustomRow>
          <Text>方案售價</Text>
          <Text>{`$${salePlan?.price}`}</Text>
        </CustomRow>
        <CustomRow>
          <Text>方案優惠價</Text>
          <CustomInputNumber
            min={0}
            onChange={(value) => handleOnSpecialPriceChange(value)}
            value={specialPrice}
            placeholder={`${salePlan?.price}`}
          />
        </CustomRow>
        <CustomRow>
          <Text>黑卡會員價</Text>
          <Space>
            <CustomInputNumber
              min={0}
              onChange={(value) => handleOnVIPPriceChange(value)}
              value={vipPrice}
              placeholder={`${salePlan && priceUtils.getDefaultVIPprice(specialPrice || salePlan.price)}`}
              disabled={!specialPrice}
            />
            <Text style={{ color: VIPGrossMargin && VIPGrossMargin <= 0 ? "red" : "#40a9ff" }}>{`(試算毛利率：${
              VIPGrossMargin ?? "-"
            }%)`}</Text>
          </Space>
        </CustomRow>
        <CustomRow>
          <Text>優惠時間</Text>
          <Radio.Group value={radio} onChange={(e) => setRadio(e.target.value)}>
            <CustomRadio marginBottom="10px" value={RadioType.DEFAULT}>
              預設
            </CustomRadio>
            <CustomRadio value={RadioType.TIMING}>
              <DatePicker.RangePicker
                locale={locale}
                format="YYYY-MM-DD HH:mm:ss"
                showTime={{
                  hideDisabledOptions: true,
                  defaultValue: [moment("00:00:00", "HH:mm:ss"), moment("23:59:59", "HH:mm:ss")],
                }}
                disabledDate={disabledDate}
                value={optionDiscountPeriod as any}
                onFocus={() => setRadio(RadioType.TIMING)}
                onChange={(val) => setOptionDiscountPeriod(val as moment.Moment[])}
              />
            </CustomRadio>
          </Radio.Group>
        </CustomRow>
        <Row justify="space-between">
          <Col>
            <Error>{errorMessage}</Error>
          </Col>
          <Col>
            <Space>
              <Button onClick={onCancel}>取消</Button>
              <Button type="primary" onClick={onSubmitCheck}>
                {isLast ? "確認" : "下一步"}
              </Button>
            </Space>
          </Col>
        </Row>
      </Wrapper>
    </PopupBackground>
  );
}
